import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, ImageIcon, Loader2, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FORMATS, drawPoster, posterBlob } from "@/lib/poster";

const btn = "inline-flex items-center justify-center gap-2 uppercase text-xs font-bold tracking-[0.15em] px-4 py-3 transition-colors duration-200";

// Renders one poster (draw(canvas) => Promise) with download + share. `name` (file name) also keys the redraw.
export const PosterPreview = ({ draw, name, format, title, testId }) => {
  const canvasRef = useRef(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const canvas = document.createElement("canvas");
    canvasRef.current = canvas;
    setUrl(null);
    drawRef.current(canvas).then(() => {
      if (!cancelled) setUrl(canvas.toDataURL("image/png"));
    }).catch(() => { if (!cancelled) setUrl(false); });
    return () => { cancelled = true; };
  }, [name]);

  const file = async () => new File([await posterBlob(canvasRef.current)], `${name}.png`, { type: "image/png" });

  const download = async () => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(await file());
    link.download = `${name}.png`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 2000);
  };

  const share = async () => {
    const f = await file();
    if (navigator.canShare?.({ files: [f] })) {
      try { await navigator.share({ files: [f], title }); } catch { /* user closed the share sheet */ }
    } else {
      await download();
      toast.success("Affiche téléchargée : ajoute-la dans ta story depuis ta galerie.");
    }
  };

  const f = FORMATS[format];
  return (
    <div className="flex flex-col gap-3 min-w-0" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-white">{f.label} <span className="text-zinc-400 font-normal">{f.hint}</span></p>
      <div className="bg-[hsl(var(--surface))] flex items-center justify-center" style={{ aspectRatio: `${f.w} / ${f.h}` }}>
        {url ? <img src={url} alt={`Affiche ${f.label}`} className="w-full h-full object-contain" data-testid={`${testId}-img`} />
          : url === false ? <p className="text-zinc-400 text-sm p-4">Affiche indisponible.</p>
          : <Loader2 className="w-6 h-6 animate-spin text-primary" />}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={download} disabled={!url} data-testid={`${testId}-download`} className={`${btn} border border-white/30 text-white hover:border-white disabled:opacity-50`}>
          <Download className="w-4 h-4" /> Télécharger
        </button>
        <button type="button" onClick={share} disabled={!url} data-testid={`${testId}-share`} className={`${btn} bg-primary text-white hover:bg-white hover:text-black disabled:opacity-50`}>
          <Share2 className="w-4 h-4" /> Partager
        </button>
      </div>
    </div>
  );
};

export const EventPoster = ({ event, className = "" }) => (
  <Dialog>
    <DialogTrigger data-testid="event-poster-btn" className={`inline-flex items-center justify-center gap-3 transition-colors duration-200 ${className}`}>
      <ImageIcon className="w-5 h-5" /> Affiche
    </DialogTrigger>
    <DialogContent className="rounded-none bg-black border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="event-poster-dialog">
      <DialogTitle className="font-display uppercase text-2xl text-white">Affiche à partager</DialogTitle>
      <DialogDescription className="text-zinc-300 text-sm -mt-2">Prête à poster sur Instagram, Facebook ou WhatsApp.</DialogDescription>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_0.62fr] gap-6 items-start">
        {["square", "story"].map((kind) => (
          <PosterPreview key={kind} format={kind} name={`lgtdz-${event.slug}-${kind}`} title={event.title} testId={`poster-${kind}`}
            draw={(canvas) => drawPoster(canvas, event, kind)} />
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
